/**
 * Fil d'activite equipe - aggregat des @mentions, notes projet, alertes.
 * Style Slack/Notion : timeline DESC + filtres par membre/projet/type.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMentionRenderer } from './components/MentionTextarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  mention: { label: 'Mention', color: '#008A8D', bg: '#008A8D15', icon: 'M16 12a4 4 0 10-8 0v1a4 4 0 008 0v-1z M12 8v8m0 0a4 4 0 01-4-4 4 4 0 014-4 4 4 0 014 4 4 4 0 01-4 4z' },
  project_note: { label: 'Note projet', color: '#A57F4F', bg: '#A57F4F15', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  pdf_note: { label: 'Note PDF', color: '#005558', bg: '#00555815', icon: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
  client_engagement: { label: 'Client actif', color: '#E89F4D', bg: '#E89F4D15', icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z' },
  alert: { label: 'Alerte', color: '#DC3545', bg: '#DC354515', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16.5c-.77 .833 .192 2.5 1.732 2.5z' },
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "a l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 604800) return `il y a ${Math.floor(diff / 86400)} j`;
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  } catch { return iso; }
};

const ActivityFeedPage = () => {
  const navigate = useNavigate();
  const renderer = useMentionRenderer();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [filterAuthor, setFilterAuthor] = useState('');
  const [filterTypes, setFilterTypes] = useState([]);
  const [filterDays, setFilterDays] = useState(14);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('days', filterDays);
      if (filterAuthor) params.set('author', filterAuthor);
      if (filterTypes.length > 0) params.set('types', filterTypes.join(','));
      const r = await axios.get(`${API}/activity/feed?${params.toString()}`);
      setItems(r.data.items || []);
      setStats(r.data.stats || {});
    } catch {} finally { setLoading(false); }
  }, [filterAuthor, filterTypes, filterDays]);

  useEffect(() => {
    fetchFeed();
    axios.get(`${API}/activity/team-members`).then(r => setMembers(r.data.members || [])).catch(() => {});
  }, [fetchFeed]);

  const toggleType = (k) => {
    setFilterTypes(prev => prev.includes(k) ? prev.filter(t => t !== k) : [...prev, k]);
  };

  const handleClickItem = (it) => {
    if (it.link) {
      try {
        if (it.link.startsWith('http')) {
          const u = new URL(it.link);
          navigate(u.pathname + u.search + u.hash);
        } else {
          navigate(it.link);
        }
      } catch { window.location.href = it.link; }
    }
  };

  return (
    <div className="p-4 lg:p-8" data-testid="activity-feed-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Fil d'activite equipe</h1>
          <p className="text-gray-500">Toutes les @mentions, notes et evenements internes en temps reel.</p>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { k: 'total', label: 'Total events', color: '#003E40' },
            { k: 'mention', label: 'Mentions @', color: '#008A8D' },
            { k: 'project_note', label: 'Notes projet', color: '#A57F4F' },
            { k: 'pdf_note', label: 'Notes PDF', color: '#005558' },
            { k: 'client_engagement', label: 'Clients actifs', color: '#E89F4D' },
          ].map(s => (
            <div key={s.k} className="realease-card p-4 text-center" data-testid={`activity-stat-${s.k}`}>
              <p className="text-2xl font-bold" style={{ color: s.color }}>{stats[s.k] || 0}</p>
              <p className="text-[11px] text-gray-500 uppercase tracking-wide mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="realease-card p-4 mb-6 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 font-semibold">Periode</label>
            <select value={filterDays} onChange={e => setFilterDays(parseInt(e.target.value))}
                    className="border border-gray-200 rounded-lg px-2 py-1 text-sm"
                    data-testid="activity-filter-days">
              <option value={1}>24 h</option>
              <option value={7}>7 j</option>
              <option value={14}>14 j</option>
              <option value={30}>30 j</option>
              <option value={90}>3 mois</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 font-semibold">Auteur</label>
            <select value={filterAuthor} onChange={e => setFilterAuthor(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1 text-sm"
                    data-testid="activity-filter-author">
              <option value="">Tous</option>
              {members.map(m => <option key={m.user_id} value={m.user_id}>{`${m.name} (${m.role})`}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-1.5 ml-auto">
            {Object.entries(KIND_META).map(([k, meta]) => (
              <button key={k} onClick={() => toggleType(k)}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-all ${filterTypes.includes(k) ? 'text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                      style={filterTypes.includes(k) ? { background: meta.color, borderColor: meta.color } : { borderColor: '#e5e7eb' }}
                      data-testid={`activity-filter-type-${k}`}>
                {meta.label}
              </button>
            ))}
            {filterTypes.length > 0 && (
              <button onClick={() => setFilterTypes([])}
                      className="text-xs text-gray-400 underline ml-2"
                      data-testid="activity-filter-reset">
                reset
              </button>
            )}
          </div>
        </div>

        {/* Timeline */}
        {loading && (
          <div className="text-center py-12">
            <div className="w-6 h-6 border-3 border-t-[#008A8D] border-gray-200 rounded-full animate-spin mx-auto"></div>
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="realease-card p-12 text-center text-gray-400" data-testid="activity-empty">
            <p>Aucun evenement sur cette periode.</p>
          </div>
        )}

        <div className="space-y-3" data-testid="activity-feed-list">
          {items.map(it => {
            const meta = KIND_META[it.kind] || KIND_META.alert;
            return (
              <button key={`${it.kind}_${it.id}`}
                      onClick={() => handleClickItem(it)}
                      className="realease-card w-full text-left p-4 hover:shadow-md transition-all flex gap-3 group"
                      data-testid={`activity-item-${it.id}`}>
                <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: meta.bg }}>
                  <svg className="w-4.5 h-4.5" fill="none" stroke={meta.color} viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={meta.icon} />
                  </svg>
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
                          style={{ background: meta.bg, color: meta.color }}>
                      {meta.label}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">{it.author_name || 'Systeme'}</span>
                    {it.project_name && (
                      <span className="text-xs text-gray-500">- projet <b className="text-gray-700">{it.project_name}</b></span>
                    )}
                    {it.source_label && !it.project_name && (
                      <span className="text-xs text-gray-500">- {it.source_label}</span>
                    )}
                    <span className="text-xs text-gray-400 ml-auto">{formatDate(it.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-3">
                    {renderer(it.text || it.title || '')}
                  </p>
                </div>
                <svg className="w-4 h-4 text-gray-300 group-hover:text-[#008A8D] mt-1 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ActivityFeedPage;
